import React, { useState, useEffect, useCallback } from 'react';
import { Save, FileText, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';
import { useSociete } from '../../context/SocieteContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const DOC_TYPES = {
  vente: [
    { key: 'contrat_location', label: 'Contrat de location' },
    { key: 'ordre_reparation', label: 'Ordre de réparation (Atelier)' },
    { key: 'ventes',      label: 'Devis / vente matériel' },
    { key: 'bl_vente',         label: 'Bon de livraison (Distribution)' },
    { key: 'retour_vente',     label: 'Bon de retour vente (BRV)' },
    { key: 'transfert',        label: 'Transfert de stock' },
  ],
  achat: [
    { key: 'bon_commande',  label: 'Bon de commande fournisseur' },
    { key: 'bon_reception', label: 'Bon de réception' },
  ],
};

const SEPARATORS = [
  { value: '-',  label: '—  Tiret  (LOC-2026-0001)' },
  { value: '/',  label: '/  Slash  (LOC/2026/0001)' },
  { value: '.',  label: '.  Point  (LOC.2026.0001)' },
  { value: '',   label: '∅  Aucun  (LOC20260001)' },
];

// ─── Préview ─────────────────────────────────────────────────────────────────

function buildPreview(cfg, agences) {
  const sep        = cfg.separator ?? '-';
  const padding    = cfg.padding ?? 4;
  const inclYear   = cfg.include_year ?? true;
  const parAgence  = cfg.par_agence ?? false;
  const prefGlobal = cfg.prefix_global || '???';
  const prefixes   = cfg.prefixes_agences || {};

  if (parAgence && agences.length > 0) {
    return agences.map(a => {
      const pref = prefixes[a.agence_id] || prefGlobal;
      const base = inclYear ? `${pref}${sep}${new Date().getFullYear()}${sep}` : `${pref}${sep}`;
      return { nom: a.nom, preview: `${base}${'1'.zfill ? '1'.padStart(padding, '0') : '0001'}` };
    });
  }
  const base = inclYear ? `${prefGlobal}${sep}${new Date().getFullYear()}${sep}` : `${prefGlobal}${sep}`;
  return [{ nom: null, preview: `${base}${'1'.padStart(padding, '0')}` }];
}

// ─── Bloc de config d'un document ────────────────────────────────────────────

function DocConfig({ docKey, label, cfg, agences, onChange }) {
  const set = (field, val) => onChange({ ...cfg, [field]: val });

  const previews = buildPreview(cfg, agences);
  const parAgence = cfg.par_agence ?? false;
  const prefixes  = cfg.prefixes_agences || {};

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: 'var(--border)' }}>
      <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{label}</p>

      {/* Ligne 1 : préfixe + séparateur */}
      <div className="flex items-center gap-3">
        <span className="w-28 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Préfixe</span>
        <input
          value={cfg.prefix_global || ''}
          onChange={e => set('prefix_global', e.target.value.toUpperCase())}
          data-testid={`prefix-${docKey}`}
          className="w-28 px-2.5 py-1.5 rounded-lg border text-sm font-mono"
          style={{ borderColor: 'var(--border)' }}
          placeholder="LOC"
        />
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Séparateur</span>
        <select
          value={cfg.separator ?? '-'}
          onChange={e => set('separator', e.target.value)}
          data-testid={`sep-${docKey}`}
          className="flex-1 px-2.5 py-1.5 rounded-lg border text-sm"
          style={{ borderColor: 'var(--border)' }}>
          {SEPARATORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Ligne 2 : année + padding + par agence */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="w-28 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Options</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={cfg.include_year ?? true} onChange={e => set('include_year', e.target.checked)}
            className="w-3.5 h-3.5 rounded" style={{ accentColor: 'var(--primary-solid)' }}
            data-testid={`year-${docKey}`} />
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Inclure l'année</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Longueur N°</span>
          <input type="number" min={1} max={8} value={cfg.padding ?? 4}
            onChange={e => set('padding', Number(e.target.value))}
            data-testid={`padding-${docKey}`}
            className="w-14 px-2 py-1 rounded-lg border text-sm text-center"
            style={{ borderColor: 'var(--border)' }} />
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={parAgence} onChange={e => set('par_agence', e.target.checked)}
            className="w-3.5 h-3.5 rounded" style={{ accentColor: 'var(--primary-solid)' }}
            data-testid={`par-agence-${docKey}`} />
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Par agence</span>
        </label>
      </div>

      {/* Préfixes par agence */}
      {parAgence && agences.length > 0 && (
        <div className="ml-[7.5rem] space-y-1.5">
          {agences.map(a => (
            <div key={a.agence_id} className="flex items-center gap-3">
              <span className="text-xs flex-shrink-0 w-40 truncate" style={{ color: 'var(--text-muted)' }}>{a.nom}</span>
              <input
                value={prefixes[a.agence_id] || ''}
                onChange={e => set('prefixes_agences', { ...prefixes, [a.agence_id]: e.target.value.toUpperCase() })}
                data-testid={`prefix-agence-${docKey}-${a.agence_id}`}
                className="w-28 px-2.5 py-1.5 rounded-lg border text-sm font-mono"
                style={{ borderColor: 'var(--border)' }}
                placeholder={cfg.prefix_global || 'Préfixe'}
              />
              <span className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold flex-shrink-0"
                style={{ background: 'var(--bg-subtle)', color: 'var(--primary-solid)', border: '1px solid var(--border)' }}>
                {buildPreview({ ...cfg, prefixes_agences: { [a.agence_id]: prefixes[a.agence_id] || cfg.prefix_global } }, [a])[0]?.preview}
              </span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Numerotation() {
  const [config, setConfig]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState('vente');

  // Agences toujours à jour depuis le contexte global
  const { societes } = useSociete();
  const agences = (societes || []).flatMap(s => (s.agences || []).map(a => ({ ...a, societe_nom: s.nom })));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfgRes = await api.getNumerotation();
      setConfig(cfgRes.data || {});
    } catch { toast.error('Erreur chargement de la numérotation'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateDoc = (docKey, val) => setConfig(prev => ({ ...prev, [docKey]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.setNumerotation(config);
      toast.success('Numérotation enregistrée');
    } catch { toast.error('Erreur enregistrement'); }
    finally { setSaving(false); }
  };

  const tabs = [
    { key: 'vente', label: 'Documents de vente', icon: FileText },
    { key: 'achat', label: "Documents d'achat",  icon: ShoppingCart },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Numérotation des documents</h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Configurez le format des numéros générés automatiquement pour chaque type de document.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--bg-subtle)', width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: tab === t.key ? 'white' : 'transparent',
              color: tab === t.key ? 'var(--text)' : 'var(--text-muted)',
              boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Configs */}
      {loading ? (
        <div className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>Chargement...</div>
      ) : (
        <div className="space-y-3">
          {DOC_TYPES[tab].map(({ key, label }) => (
            <DocConfig
              key={key}
              docKey={key}
              label={label}
              cfg={config[key] || {}}
              agences={agences}
              onChange={val => updateDoc(key, val)}
            />
          ))}
        </div>
      )}

      {/* Enregistrer */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving || loading}
          data-testid="save-numerotation-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: 'var(--primary-gradient)' }}>
          <Save size={15} />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
